// ─── src/AwardsInvitationsView.tsx ───────────────────────────────────────────
//
// Startup Syria Awards 2026 guest list. Staff upload a CSV of guests, email
// everyone their personal invitation link, send the same link on WhatsApp by
// hand, and watch who opened / confirmed / declined.
import React, { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BellRing,
  CheckCircle2,
  Copy,
  Download,
  Eye,
  FileDown,
  Mail,
  MessageCircle,
  MessageSquareText,
  Search,
  Send,
  Trash2,
  Upload,
  UserPlus,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { StatCard } from "./components/ui/StatCard";
import { TableSkeleton } from "./components/ui/Skeleton";
import {
  awardsInvitationsApi,
  type AwardsInvitation,
  type GuestCategory,
  type ImportResult,
  type InvitationRow,
  type InvitationStatus,
} from "./api/awardsInvitations";

const KEY = ["awardsInvitations"];

const STATUS_LABEL: Record<InvitationStatus, string> = {
  pending: "Not sent",
  sent: "Sent",
  opened: "Opened",
  confirmed: "Confirmed",
  declined: "Declined",
};
const STATUS_STYLE: Record<InvitationStatus, string> = {
  pending: "bg-brand-border/40 text-brand-muted border border-brand-border",
  sent: "bg-sky-500/10 text-sky-300 border border-sky-500/30",
  opened: "bg-amber-500/10 text-amber-300 border border-amber-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  declined: "bg-rose-500/10 text-rose-300 border border-rose-500/30",
};

export default function AwardsInvitationsView() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<InvitationStatus | "all" | "no-answer">("all");
  const [categoryFilter, setCategoryFilter] = useState<GuestCategory | "all" | "none">("all");
  const [confirmBulk, setConfirmBulk] = useState<"send" | "remind" | null>(null);
  const [notice, setNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const examplesSkipped = useRef(0);

  const { data, isLoading, isError } = useQuery({
    queryKey: KEY,
    queryFn: awardsInvitationsApi.list,
    // Poll while a bulk email job runs so progress and statuses update live.
    refetchInterval: (q) => (q.state.data?.job ? 3000 : 30_000),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const fail = (err: unknown) =>
    setNotice({ tone: "err", text: err instanceof Error ? err.message : "Something went wrong." });

  const [addOpen, setAddOpen] = useState(false);
  // Single guest added by hand: same import endpoint, one row, so matching
  // (email → phone → name) and link generation behave exactly like a CSV.
  const addMut = useMutation({
    mutationFn: (row: InvitationRow) => awardsInvitationsApi.import([row]),
    onSuccess: (res, row) => {
      if (res.skipped.length) {
        setNotice({ tone: "err", text: `Couldn't add ${row.fullName}: ${res.skipped[0].reason}.` });
        return;
      }
      setNotice({
        tone: "ok",
        text: res.created
          ? `Added ${row.fullName}. Their personal link is ready in the list below.`
          : `${row.fullName} was already on the list, so their details were updated and their link kept.`,
      });
      setAddOpen(false);
      setSearch(row.fullName);
      setFilter("all");
      refresh();
    },
    onError: fail,
  });

  const importMut = useMutation({
    mutationFn: awardsInvitationsApi.import,
    onSuccess: (res) => {
      setImportResult(res);
      const ex = examplesSkipped.current;
      setNotice({
        tone: "ok",
        text: `Imported: ${res.created} new, ${res.updated} updated, ${res.skipped.length} skipped.` +
          (ex ? ` Ignored ${ex} template example row(s).` : ""),
      });
      refresh();
    },
    onError: fail,
  });
  const sendMut = useMutation({
    mutationFn: (ids?: string[]) => awardsInvitationsApi.send(ids),
    onSuccess: (res) => {
      setNotice({ tone: "ok", text: res.queued ? `Sending ${res.queued} invitation email(s)…` : "Nothing to send." });
      refresh();
    },
    onError: fail,
  });
  const remindMut = useMutation({
    mutationFn: awardsInvitationsApi.remind,
    onSuccess: (res) => {
      setNotice({ tone: "ok", text: res.queued ? `Sending ${res.queued} reminder(s)…` : "Everyone invited has answered." });
      refresh();
    },
    onError: fail,
  });
  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof awardsInvitationsApi.update>[1] }) =>
      awardsInvitationsApi.update(id, patch),
    onSuccess: refresh,
    onError: fail,
  });
  const deleteMut = useMutation({
    mutationFn: awardsInvitationsApi.remove,
    onSuccess: refresh,
    onError: fail,
  });

  // ── Multi-select ──
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [armBulkDelete, setArmBulkDelete] = useState(false);
  const toggleOne = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  /** Same change to every selected guest, a few requests at a time. */
  const bulkMut = useMutation({
    mutationFn: async ({ ids, run }: { ids: string[]; run: (id: string) => Promise<unknown>; done: string }) => {
      let failed = 0;
      for (let i = 0; i < ids.length; i += 8) {
        const res = await Promise.allSettled(ids.slice(i, i + 8).map(run));
        failed += res.filter((r) => r.status === "rejected").length;
      }
      return { total: ids.length, failed };
    },
    onSuccess: ({ total, failed }, { done }) => {
      setNotice(
        failed
          ? { tone: "err", text: `${done} ${total - failed} of ${total} guests. ${failed} failed, try them again.` }
          : { tone: "ok", text: `${done} ${total} guest(s).` },
      );
      refresh();
    },
    onError: fail,
  });
  const bulkUpdate = (patch: Parameters<typeof awardsInvitationsApi.update>[1], done: string) =>
    bulkMut.mutate({ ids: Array.from(selected), run: (id) => awardsInvitationsApi.update(id, patch), done });

  const items = data?.items ?? [];
  const stats = data?.stats;
  const job = data?.job ?? null;
  const notEmailed = items.filter((i) => i.email && !i.emailSentAt).length;
  const unanswered = items.filter((i) => !i.rsvp && (i.emailSentAt || i.whatsappSentAt)).length;
  const deadlinePassed = data ? Date.now() > new Date(data.rsvpClosesAt).getTime() : false;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (filter === "no-answer" && i.rsvp) return false;
      if (filter !== "all" && filter !== "no-answer" && i.status !== filter) return false;
      if (categoryFilter === "none" && i.category) return false;
      if (categoryFilter !== "all" && categoryFilter !== "none" && i.category !== categoryFilter) return false;
      if (!q) return true;
      return [i.fullName, i.fullNameEn, i.email, i.phone].some((v) => v?.toLowerCase().includes(q));
    });
  }, [items, search, filter, categoryFilter]);

  const selectedRows = items.filter((i) => selected.has(i.id));
  const allShownSelected = filtered.length > 0 && filtered.every((i) => selected.has(i.id));
  const someShownSelected = filtered.some((i) => selected.has(i.id));
  const toggleAllShown = () =>
    setSelected((s) => {
      const n = new Set(s);
      for (const i of filtered) {
        if (allShownSelected) n.delete(i.id);
        else n.add(i.id);
      }
      return n;
    });

  const categoryCount = (c: GuestCategory | "all" | "none") =>
    c === "all" ? items.length : items.filter((i) => (c === "none" ? !i.category : i.category === c)).length;

  const onFile = async (file: File | null) => {
    if (!file) return;
    setImportResult(null);
    try {
      const parsed = parseGuestCsv(await file.text());
      const rows = parsed.filter((r) => !isExampleRow(r));
      examplesSkipped.current = parsed.length - rows.length;
      if (rows.length === 0) {
        setNotice({ tone: "err", text: "No rows found. The CSV needs a header row with at least name and email columns." });
        return;
      }
      importMut.mutate(rows);
    } catch (err) {
      fail(err);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Guests" value={stats?.total ?? 0} icon={Users} accent="bg-brand-orange" />
        <StatCard label="Confirmed" value={stats?.confirmed ?? 0} icon={CheckCircle2} accent="bg-emerald-600" />
        <StatCard label="Declined" value={stats?.declined ?? 0} icon={XCircle} accent="bg-rose-600" />
        <StatCard label="Opened, no answer" value={stats?.opened ?? 0} icon={Eye} accent="bg-amber-600" />
        <StatCard label="Not opened yet" value={(stats?.sent ?? 0) + (stats?.pending ?? 0)} icon={Mail} accent="bg-brand-teal" />
      </div>

      {/* Actions */}
      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={importMut.isPending}
            className="inline-flex items-center gap-2 rounded-lg border border-brand-border px-3 py-2 text-sm text-white hover:border-brand-green disabled:opacity-50"
          >
            <Upload size={14} /> {importMut.isPending ? "Importing…" : "Upload guest CSV"}
          </button>
          <button
            type="button"
            onClick={downloadTemplate}
            className="inline-flex items-center gap-2 rounded-lg border border-brand-border px-3 py-2 text-sm text-brand-muted hover:text-white"
          >
            <FileDown size={14} /> Download CSV template
          </button>
          <button
            type="button"
            onClick={() => setAddOpen((v) => !v)}
            aria-expanded={addOpen}
            className="inline-flex items-center gap-2 rounded-lg border border-brand-border px-3 py-2 text-sm text-white hover:border-brand-green"
          >
            <UserPlus size={14} /> Add guest
          </button>

          <BulkButton
            icon={<Send size={14} />}
            label={`Email invitations (${notEmailed} not emailed)`}
            confirmLabel={`Send ${notEmailed} emails now?`}
            disabled={!notEmailed || !!job || sendMut.isPending}
            armed={confirmBulk === "send"}
            onArm={() => setConfirmBulk("send")}
            onCancel={() => setConfirmBulk(null)}
            onConfirm={() => { setConfirmBulk(null); sendMut.mutate(undefined); }}
          />
          <BulkButton
            icon={<BellRing size={14} />}
            label={`Remind unanswered (${unanswered})`}
            confirmLabel={`Email a reminder to ${unanswered} guests?`}
            disabled={!unanswered || !!job || remindMut.isPending || deadlinePassed}
            armed={confirmBulk === "remind"}
            onArm={() => setConfirmBulk("remind")}
            onCancel={() => setConfirmBulk(null)}
            onConfirm={() => { setConfirmBulk(null); remindMut.mutate(); }}
          />
          <button
            type="button"
            onClick={() => exportCsv(items)}
            disabled={!items.length}
            className="ml-auto inline-flex items-center gap-2 rounded-lg border border-brand-border px-3 py-2 text-sm text-brand-muted hover:text-white disabled:opacity-50"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>

        {addOpen && (
          <AddGuestForm
            saving={addMut.isPending}
            onCancel={() => setAddOpen(false)}
            onSave={(row) => addMut.mutate(row)}
          />
        )}

        <p className="text-xs text-brand-muted">
          Start from the template: keep its first row as is and add one guest per row.{" "}
          Only <code className="text-white">name</code> is required.{" "}
          <code className="text-white">email</code>, <code className="text-white">name_en</code>,{" "}
          <code className="text-white">gender</code> (male or female, sets السيد / السيدة and Mr. / Ms.),{" "}
          <code className="text-white">category</code> (catalyst, partner or vip; sets the RSVP deadline: 2 Nov for
          catalyst and partner, 4 Nov for Seniors + VIP),{" "}
          <code className="text-white">phone</code> (with country code, e.g. +963 944 123 456) and{" "}
          <code className="text-white">lang</code> (ar or en, default ar) are optional. Guests without an email
          still get a personal link: use <em>Copy invitation message</em> to send it yourself.
          The two example rows are ignored on upload. Re-uploading the same guest (same email, or same phone,
          or same name when neither is given) updates them and keeps their link.
          {data && <> RSVP closes {new Date(data.rsvpClosesAt).toLocaleString()}.</>}
        </p>

        {job && (
          <div className="rounded-lg border border-brand-border p-3">
            <div className="flex justify-between text-xs text-brand-muted">
              <span>{job.kind === "invite" ? "Sending invitations" : "Sending reminders"}…</span>
              <span>{job.done} / {job.total}{job.failed ? ` · ${job.failed} failed` : ""}</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-brand-border/50 overflow-hidden">
              <div className="h-full bg-brand-green transition-all" style={{ width: `${(job.done / job.total) * 100}%` }} />
            </div>
          </div>
        )}

        {notice && (
          <div className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2 text-sm ${notice.tone === "ok" ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"}`}>
            <span>{notice.text}</span>
            <button onClick={() => setNotice(null)} aria-label="Dismiss"><X size={14} /></button>
          </div>
        )}
        {importResult && importResult.skipped.length > 0 && (
          <details className="text-xs text-brand-muted">
            <summary className="cursor-pointer text-amber-300">{importResult.skipped.length} row(s) skipped, see why</summary>
            <ul className="mt-2 space-y-1">
              {importResult.skipped.map((s) => (
                <li key={`${s.row}-${s.email}`}>Row {s.row}: {s.email || "(no email)"} · {s.reason}</li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm w-full">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(["all", "no-answer", "pending", "sent", "opened", "confirmed", "declined"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === f ? "bg-brand-green text-gray-900" : "border border-brand-border text-brand-muted hover:text-white"}`}
            >
              {f === "all" ? "All" : f === "no-answer" ? "No answer" : STATUS_LABEL[f]}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 sm:ml-auto" role="group" aria-label="Category">
          {(["all", "catalyst", "partner", "vip", "none"] as const).map((c) => (
            <button
              key={c}
              onClick={() => setCategoryFilter(c)}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${categoryFilter === c ? "bg-white text-gray-900" : "border border-brand-border text-brand-muted hover:text-white"}`}
            >
              {c === "all" ? "All categories" : c === "none" ? "No category" : CATEGORY_LABEL[c]} · {categoryCount(c)}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk actions for the selected guests */}
      {selectedRows.length > 0 && (
        <div className="card sticky top-2 z-10 p-3 flex flex-wrap items-center gap-2 border border-brand-green/40">
          <span className="text-sm font-semibold text-white">{selectedRows.length} selected</span>
          <button onClick={() => setSelected(new Set())} className="text-xs text-brand-muted hover:text-white">Clear</button>
          <span className="mx-1 h-5 w-px bg-brand-border" aria-hidden="true" />
          <button
            onClick={() => exportCsv(selectedRows)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-green px-3 py-1.5 text-xs font-semibold text-gray-900"
            title="CSV of the selected guests, with a Message column holding each one's WhatsApp message"
          >
            <Download size={13} /> Download with messages
          </button>
          <select
            value=""
            disabled={bulkMut.isPending}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              bulkUpdate({ category: v === "none" ? null : (v as GuestCategory) }, "Category set for");
            }}
            className="rounded-lg border border-brand-border bg-brand-navy px-2 py-1.5 text-xs text-white"
            aria-label="Set category for selected guests"
          >
            <option value="">Set category…</option>
            {(Object.keys(CATEGORY_LABEL) as GuestCategory[]).map((c) => (
              <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
            ))}
            <option value="none">No category</option>
          </select>
          <select
            value=""
            disabled={bulkMut.isPending}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              bulkUpdate({ gender: v === "none" ? null : (v as "male" | "female") }, "Gender set for");
            }}
            className="rounded-lg border border-brand-border bg-brand-navy px-2 py-1.5 text-xs text-white"
            aria-label="Set gender for selected guests"
          >
            <option value="">Set gender…</option>
            <option value="male">Male · السيد</option>
            <option value="female">Female · السيدة</option>
            <option value="none">Not set</option>
          </select>
          <button
            disabled={bulkMut.isPending}
            onClick={() => bulkUpdate({ whatsappSent: true }, "Marked as sent on WhatsApp:")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
          >
            <MessageCircle size={13} /> Mark WhatsApp sent
          </button>
          <button
            disabled={bulkMut.isPending}
            onClick={() => bulkUpdate({ whatsappSent: false }, "Unmarked WhatsApp for")}
            className="rounded-lg border border-brand-border px-3 py-1.5 text-xs text-brand-muted hover:text-white disabled:opacity-50"
          >
            Unmark WhatsApp
          </button>
          {selectedRows.some((r) => r.email) && (
            <button
              disabled={!!job || sendMut.isPending}
              onClick={() => sendMut.mutate(selectedRows.filter((r) => r.email).map((r) => r.id))}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-xs text-white hover:border-brand-green disabled:opacity-50"
              title="Sends (or resends) the invitation email to the selected guests who have an email"
            >
              <Send size={13} /> Email ({selectedRows.filter((r) => r.email).length})
            </button>
          )}
          {armBulkDelete ? (
            <span className="inline-flex items-center gap-2 text-xs text-rose-200">
              Delete {selectedRows.length} guest(s) and their links?
              <button
                onClick={() => {
                  setArmBulkDelete(false);
                  const ids = Array.from(selected);
                  bulkMut.mutate(
                    { ids, run: (id) => awardsInvitationsApi.remove(id), done: "Deleted" },
                    { onSuccess: () => setSelected(new Set()) },
                  );
                }}
                className="rounded bg-rose-600 px-2 py-1 font-semibold text-white"
              >
                Delete
              </button>
              <button onClick={() => setArmBulkDelete(false)} className="text-brand-muted hover:text-white">Cancel</button>
            </span>
          ) : (
            <button
              disabled={bulkMut.isPending}
              onClick={() => setArmBulkDelete(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-xs text-brand-muted hover:text-rose-300 disabled:opacity-50"
            >
              <Trash2 size={13} /> Delete
            </button>
          )}
          {bulkMut.isPending && <span className="text-xs text-brand-muted">Working…</span>}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">Couldn't load the guest list.</div>
      ) : filtered.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">
          {items.length ? "No guests match this filter." : "No guests yet. Upload a CSV to start."}
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border text-left text-xs uppercase tracking-wider text-brand-muted">
                <th className="w-8 py-3 pl-3">
                  <input
                    type="checkbox"
                    checked={allShownSelected}
                    ref={(el) => { if (el) el.indeterminate = !allShownSelected && someShownSelected; }}
                    onChange={toggleAllShown}
                    className="accent-emerald-500"
                    aria-label="Select all guests shown"
                  />
                </th>
                <th className="px-3 py-3">Guest</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">WhatsApp</th>
                <th className="px-3 py-3">Link &amp; message</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <GuestRow
                  key={g.id}
                  g={g}
                  checked={selected.has(g.id)}
                  onToggle={() => toggleOne(g.id)}
                  busy={updateMut.isPending || deleteMut.isPending}
                  onUpdate={(patch) => updateMut.mutate({ id: g.id, patch })}
                  onResend={() => sendMut.mutate([g.id])}
                  onDelete={() => deleteMut.mutate(g.id)}
                  jobRunning={!!job}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GuestRow({
  g,
  checked,
  onToggle,
  busy,
  jobRunning,
  onUpdate,
  onResend,
  onDelete,
}: {
  g: AwardsInvitation;
  checked: boolean;
  onToggle: () => void;
  busy: boolean;
  jobRunning: boolean;
  onUpdate: (patch: Parameters<typeof awardsInvitationsApi.update>[1]) => void;
  onResend: () => void;
  onDelete: () => void;
}) {
  const [copied, setCopied] = useState<"link" | "message" | null>(null);
  const [armDelete, setArmDelete] = useState(false);
  const wa = whatsappUrl(g);

  const copy = async (text: string, what: "link" | "message") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      window.prompt(what === "link" ? "Copy this link" : "Copy this message", text);
    }
  };

  return (
    <tr className={`border-b border-brand-border/40 align-top ${checked ? "bg-emerald-500/5" : ""}`}>
      <td className="w-8 py-3 pl-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-1 accent-emerald-500"
          aria-label={`Select ${g.fullName}`}
        />
      </td>
      <td className="px-3 py-3">
        <p className="font-semibold text-white" dir="auto">{g.fullName}</p>
        {g.fullNameEn && <p className="text-xs text-brand-muted">{g.fullNameEn}</p>}
        <div className="mt-1 flex items-center gap-2 text-xs text-brand-muted">
          <span>{g.lang === "en" ? "English" : "Arabic"}</span>
          <select
            value={g.gender ?? ""}
            disabled={busy}
            onChange={(e) => onUpdate({ gender: (e.target.value || null) as "male" | "female" | null })}
            className="rounded border border-brand-border bg-brand-navy px-1.5 py-0.5 text-xs text-white"
            title="Sets the salutation on the invitation: السيد / Mr. or السيدة / Ms."
            aria-label="Gender"
          >
            <option value="">Gender: not set</option>
            <option value="male">Male · السيد / Mr.</option>
            <option value="female">Female · السيدة / Ms.</option>
          </select>
          <select
            value={g.category ?? ""}
            disabled={busy}
            onChange={(e) => onUpdate({ category: (e.target.value || null) as GuestCategory | null })}
            className={`rounded border bg-brand-navy px-1.5 py-0.5 text-xs text-white ${g.category ? "border-brand-border" : "border-amber-500/60"}`}
            title="Sets the RSVP deadline and the WhatsApp message"
            aria-label="Category"
          >
            <option value="">Category: not set</option>
            {(Object.keys(CATEGORY_LABEL) as GuestCategory[]).map((c) => (
              <option key={c} value={c}>{CATEGORY_LABEL[c]} · by {DEADLINE[c].en}</option>
            ))}
          </select>
        </div>
      </td>
      <td className="px-3 py-3">
        <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[g.status]}`}>
          {STATUS_LABEL[g.status]}
        </span>
        {g.openedAt && <p className="mt-1 text-[11px] text-brand-muted">Opened {fmt(g.openedAt)}{g.openCount > 1 ? ` · ${g.openCount}×` : ""}</p>}
        {g.rsvpAt && <p className="text-[11px] text-brand-muted">Answered {fmt(g.rsvpAt)}</p>}
      </td>
      <td className="px-3 py-3">
        {!g.email ? (
          <p className="text-[11px] text-brand-muted">No email · share the link by hand</p>
        ) : (<>
        <p className="text-xs text-white">{g.email}</p>
        {g.lastEmailError ? (
          <p className="text-[11px] text-rose-300" title={g.lastEmailError}>Failed to send</p>
        ) : g.emailSentAt ? (
          <p className="text-[11px] text-brand-muted">
            Sent {fmt(g.emailSentAt)}{g.reminderCount ? ` · ${g.reminderCount} reminder(s)` : ""}
          </p>
        ) : (
          <p className="text-[11px] text-brand-muted">Not emailed</p>
        )}
        </>)}
      </td>
      <td className="px-3 py-3">
        {g.phone ? (
          <div className="space-y-1">
            <a
              href={wa ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-500/10"
            >
              <MessageCircle size={12} /> {g.phone}
            </a>
            <label className="flex items-center gap-1.5 text-[11px] text-brand-muted cursor-pointer">
              <input
                type="checkbox"
                checked={!!g.whatsappSentAt}
                disabled={busy}
                onChange={(e) => onUpdate({ whatsappSent: e.target.checked })}
                className="accent-emerald-500"
              />
              {g.whatsappSentAt ? `Sent ${fmt(g.whatsappSentAt)}` : "Mark as sent"}
            </label>
          </div>
        ) : (
          <span className="text-xs text-brand-muted">No phone</span>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="space-y-1.5">
          <button onClick={() => copy(g.link, "link")} className="flex items-center gap-1.5 text-xs text-brand-muted hover:text-white">
            <Copy size={12} /> {copied === "link" ? "Copied" : "Copy link"}
          </button>
          <button
            onClick={() => copy(inviteMessage(g), "message")}
            className="flex items-center gap-1.5 text-xs text-brand-muted hover:text-white"
            title="Copies the ready-to-send invitation text with this guest's link"
          >
            <MessageSquareText size={12} /> {copied === "message" ? "Copied" : "Copy invitation message"}
          </button>
          {!g.phone && (
            <label className="flex items-center gap-1.5 text-[11px] text-brand-muted cursor-pointer">
              <input
                type="checkbox"
                checked={!!g.whatsappSentAt}
                disabled={busy}
                onChange={(e) => onUpdate({ whatsappSent: e.target.checked })}
                className="accent-emerald-500"
              />
              {g.whatsappSentAt ? `Shared ${fmt(g.whatsappSentAt)}` : "Mark as shared"}
            </label>
          )}
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap justify-end gap-1.5">
          <select
            value={g.rsvp ?? ""}
            disabled={busy}
            onChange={(e) => onUpdate({ rsvp: (e.target.value || null) as "confirmed" | "declined" | null })}
            className="rounded-lg border border-brand-border bg-brand-navy px-2 py-1 text-xs text-white"
            title="Set the answer by hand, e.g. a guest who confirmed by phone"
          >
            <option value="">No answer</option>
            <option value="confirmed">Confirmed</option>
            <option value="declined">Declined</option>
          </select>
          {g.email && <button
            onClick={onResend}
            disabled={jobRunning}
            className="inline-flex items-center gap-1 rounded-lg border border-brand-border px-2 py-1 text-xs text-brand-muted hover:text-white disabled:opacity-50"
            title={g.emailSentAt ? "Send the invitation email again" : "Send the invitation email"}
          >
            <Send size={12} /> {g.emailSentAt ? "Resend" : "Email"}
          </button>}
          {armDelete ? (
            <>
              <button onClick={onDelete} className="rounded-lg bg-rose-600 px-2 py-1 text-xs text-white">Delete</button>
              <button onClick={() => setArmDelete(false)} className="rounded-lg border border-brand-border px-2 py-1 text-xs text-brand-muted">Cancel</button>
            </>
          ) : (
            <button onClick={() => setArmDelete(true)} className="rounded-lg border border-brand-border px-2 py-1 text-xs text-brand-muted hover:text-rose-300" aria-label="Delete guest">
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function AddGuestForm({
  saving,
  onSave,
  onCancel,
}: {
  saving: boolean;
  onSave: (row: InvitationRow) => void;
  onCancel: () => void;
}) {
  const [fullName, setFullName] = useState("");
  const [fullNameEn, setFullNameEn] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [lang, setLang] = useState<"ar" | "en">("ar");
  const [gender, setGender] = useState<"" | "male" | "female">("");
  const [category, setCategory] = useState<"" | GuestCategory>("");
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = fullName.trim();
    const mail = email.trim();
    if (!name) return setError("Enter the guest's name.");
    if (mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) return setError("That email address doesn't look right.");
    setError(null);
    onSave({
      fullName: name,
      fullNameEn: fullNameEn.trim() || undefined,
      email: mail || undefined,
      phone: phone.trim() || undefined,
      lang,
      gender: gender || undefined,
      category: category || undefined,
    });
  };

  const input =
    "w-full rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-sm text-white placeholder-brand-muted focus:outline-none focus:border-brand-green";
  return (
    <form onSubmit={submit} className="rounded-lg border border-brand-border p-4 space-y-3" noValidate>
      <p className="text-sm font-semibold text-white">Add a guest</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="space-y-1 text-xs text-brand-muted">
          <span>Name *</span>
          <input id="ag-name" dir="auto" value={fullName} onChange={(e) => setFullName(e.target.value)} className={input} placeholder="الاسم الكامل" autoFocus />
        </label>
        <label className="space-y-1 text-xs text-brand-muted">
          <span>Name in English (optional)</span>
          <input id="ag-name-en" value={fullNameEn} onChange={(e) => setFullNameEn(e.target.value)} className={input} placeholder="Shown on the English card" />
        </label>
        <label className="space-y-1 text-xs text-brand-muted">
          <span>Email (optional)</span>
          <input id="ag-email" type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} className={input} placeholder="name@example.org" />
        </label>
        <label className="space-y-1 text-xs text-brand-muted">
          <span>Phone / WhatsApp (optional)</span>
          <input id="ag-phone" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} className={input} placeholder="+963 944 123 456" />
        </label>
        <label className="space-y-1 text-xs text-brand-muted">
          <span>Gender (for السيد / السيدة)</span>
          <select id="ag-gender" value={gender} onChange={(e) => setGender(e.target.value as "" | "male" | "female")} className={input}>
            <option value="">Not set (neutral)</option>
            <option value="male">Male · السيد / Mr.</option>
            <option value="female">Female · السيدة / Ms.</option>
          </select>
        </label>
        <label className="space-y-1 text-xs text-brand-muted">
          <span>Category (sets the RSVP deadline)</span>
          <select id="ag-category" value={category} onChange={(e) => setCategory(e.target.value as "" | GuestCategory)} className={input}>
            <option value="">Not set (by {DEADLINE.none.en})</option>
            {(Object.keys(CATEGORY_LABEL) as GuestCategory[]).map((c) => (
              <option key={c} value={c}>{CATEGORY_LABEL[c]} · by {DEADLINE[c].en}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1 text-xs text-brand-muted">
          <span>Invitation language</span>
          <select id="ag-lang" value={lang} onChange={(e) => setLang(e.target.value as "ar" | "en")} className={input}>
            <option value="ar">Arabic</option>
            <option value="en">English</option>
          </select>
        </label>
      </div>
      {error && <p className="text-xs text-rose-300" role="alert">{error}</p>}
      <p className="text-[11px] text-brand-muted">
        Saving creates their personal invitation link right away. Nothing is sent automatically: email it from
        the list, or use Copy invitation message / WhatsApp.
      </p>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="rounded-lg bg-brand-green px-4 py-2 text-sm font-semibold text-gray-900 disabled:opacity-50">
          {saving ? "Saving…" : "Add guest"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-brand-border px-4 py-2 text-sm text-brand-muted hover:text-white">
          Cancel
        </button>
      </div>
    </form>
  );
}

function BulkButton({
  icon, label, confirmLabel, disabled, armed, onArm, onCancel, onConfirm,
}: {
  icon: React.ReactNode; label: string; confirmLabel: string; disabled: boolean;
  armed: boolean; onArm: () => void; onCancel: () => void; onConfirm: () => void;
}) {
  if (armed) {
    return (
      <span className="inline-flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-200">
        {confirmLabel}
        <button onClick={onConfirm} className="rounded bg-brand-green px-2 py-0.5 text-xs font-semibold text-gray-900">Yes, send</button>
        <button onClick={onCancel} className="text-xs text-brand-muted hover:text-white">Cancel</button>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onArm}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-lg bg-brand-green px-3 py-2 text-sm font-semibold text-gray-900 disabled:opacity-40"
    >
      {icon} {label}
    </button>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** wa.me needs digits with country code. Local Syrian numbers (09…) get 963. */
function waNumber(phone: string): string | null {
  let d = phone.replace(/[^\d+]/g, "");
  if (d.startsWith("+")) d = d.slice(1);
  else if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = "963" + d.slice(1);
  return d.length >= 8 ? d : null;
}

const CATEGORY_LABEL: Record<GuestCategory, string> = {
  catalyst: "Catalyst",
  partner: "Partners",
  vip: "Seniors + VIP",
};

/** RSVP deadline per category; must match awards-deadlines.ts in the backend. */
const DEADLINE: Record<GuestCategory | "none", { ar: string; en: string }> = {
  catalyst: { ar: "4 تشرين الثاني", en: "November 4" },
  partner: { ar: "4 تشرين الثاني", en: "November 4" },
  vip: { ar: "4 تشرين الثاني", en: "November 4" },
  none: { ar: "4 تشرين الثاني", en: "November 4" },
};

/** Default invitation text with the guest's personal link (WhatsApp + copy). */
function inviteMessage(g: AwardsInvitation): string {
  const titleEn = g.gender === "male" ? "Mr. " : g.gender === "female" ? "Ms. " : "";
  const greetAr = g.gender === "male" ? `حضرة السيد ${g.fullName} المحترم،` : g.gender === "female" ? `حضرة السيدة ${g.fullName} المحترمة،` : `حضرة ${g.fullName} المحترم/ة،`;
  const by = DEADLINE[g.category ?? "none"];
  // Catalysts get two extra notes: travel compensation for those coming from
  // outside Damascus, and (important) the invitation stays off social media.
  const noteAr = g.category === "catalyst"
    ? "\n\nللقادمين من خارج دمشق: سيتم تقديم تعويض عن تكاليف السفر بحدود 20 دولاراً.\n\nملاحظة هامة: يرجى عدم نشر هذه الدعوة أو مشاركتها على وسائل التواصل الاجتماعي."
    : "";
  const noteEn = g.category === "catalyst"
    ? "\n\nIf you are coming from outside Damascus, you will receive travel compensation of about $20.\n\nImportant: please do not post or share this invitation on social media."
    : "";
  return g.lang === "en"
      ? `Dear ${titleEn}${g.fullNameEn || g.fullName},\n\nUnder the patronage of the Ministry of Communications and Information Technology, we are honored to invite you to the Startup Syria Awards Ceremony 2026 on Thursday, November 5, 5:00–8:00 PM at Rawaa Restaurant, next to Dama Rose, Beirut Street, Damascus.\n\nHere is your personal invitation. Please open it and confirm your attendance by ${by.en}:\n${g.link}\n\nThis invitation is personal to you and cannot be transferred.${noteEn}`
      : `${greetAr}\n\nتحت رعاية معالي وزير الاتصالات السيد عبد السلام هيكل تدعوكم وزارة الاتصالات وتقانة المعلومات لحضور حفل جوائز ستارت أب سوريا 2026، يوم الخميس 5 تشرين الثاني من الساعة 5:00 حتى 8:00 مساءً في دمشق - شارع بيروت، مطعم رواء - جانب داما روز.\n\nللمشاركة وتأكيد الحضور يرجى فتح الرابط أدناه حتى تاريخ ${by.ar}:\n${g.link}\n\nالدعوة خاصة بكم ولا يمكن تحويلها.${noteAr}`;
}

function whatsappUrl(g: AwardsInvitation): string | null {
  if (!g.phone) return null;
  const n = waNumber(g.phone);
  if (!n) return null;
  return `https://wa.me/${n}?text=${encodeURIComponent(inviteMessage(g))}`;
}

/** Minimal RFC-4180 CSV parser (quotes, commas, newlines in quotes). */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const s = text.replace(/^﻿/, "");
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"' && s[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && s[i + 1] === "\n") i++;
      row.push(cell); cell = "";
      if (row.some((c) => c.trim())) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim())) rows.push(row);
  return rows;
}

const HEADER_ALIASES: Record<keyof InvitationRow, string[]> = {
  fullName: ["name", "full name", "fullname", "full_name", "الاسم", "الاسم الكامل"],
  fullNameEn: ["name_en", "name en", "english name", "fullnameen", "الاسم بالانكليزية", "الاسم بالإنجليزية"],
  email: ["email", "e-mail", "email address", "البريد", "البريد الإلكتروني", "الايميل", "الإيميل"],
  phone: ["phone", "phone number", "mobile", "whatsapp", "الهاتف", "رقم الهاتف", "الموبايل", "واتساب"],
  lang: ["lang", "language", "اللغة"],
  gender: ["gender", "sex", "title", "الجنس", "الجندر", "اللقب"],
  category: ["category", "group", "type", "الفئة", "التصنيف", "المجموعة"],
};

/** catalyst / partner / vip from how a sheet might say it; anything else = not set. */
function parseCategory(raw: string): GuestCategory | undefined {
  const v = raw.trim().toLowerCase();
  if (!v) return undefined;
  if (v.includes("catalyst") || v.includes("محفز") || v.includes("محفّز")) return "catalyst";
  if (v.includes("partner") || v.includes("شريك") || v.includes("شركاء")) return "partner";
  if (v.includes("vip") || v.includes("senior") || v.includes("كبار")) return "vip";
  return undefined;
}

/** male / female from the many ways a sheet might say it; anything else = not set. */
function parseGender(raw: string): "male" | "female" | undefined {
  const v = raw.trim().toLowerCase().replace(/[.\s]/g, "");
  if (["male", "m", "man", "mr", "ذكر", "رجل", "السيد", "سيد"].includes(v)) return "male";
  if (["female", "f", "woman", "ms", "mrs", "miss", "أنثى", "انثى", "امرأة", "السيدة", "سيدة", "الانسة", "الآنسة"].includes(v)) return "female";
  return undefined;
}

function parseGuestCsv(text: string): InvitationRow[] {
  const [header, ...body] = parseCsv(text);
  if (!header) return [];
  const norm = (h: string) => h.trim().toLowerCase();
  const col = (key: keyof InvitationRow) => header.findIndex((h) => HEADER_ALIASES[key].includes(norm(h)));
  const idx = { fullName: col("fullName"), fullNameEn: col("fullNameEn"), email: col("email"), phone: col("phone"), lang: col("lang"), gender: col("gender"), category: col("category") };
  if (idx.fullName < 0) {
    throw new Error("The CSV must have a name column in its first row.");
  }
  const at = (r: string[], i: number) => (i >= 0 ? (r[i] ?? "").trim() : "");
  return body.map((r) => {
    const lang = at(r, idx.lang).toLowerCase();
    return {
      fullName: at(r, idx.fullName),
      fullNameEn: at(r, idx.fullNameEn) || undefined,
      email: at(r, idx.email) || undefined,
      phone: at(r, idx.phone) || undefined,
      lang: lang === "en" || lang === "english" ? "en" : "ar",
      gender: parseGender(at(r, idx.gender)),
      category: parseCategory(at(r, idx.category)),
    };
  });
}

// ── Template ─────────────────────────────────────────────────────────────────
// The canonical upload format. Example rows use example.com addresses and are
// dropped on upload, so a forgotten example never becomes a real guest.
const TEMPLATE_HEADER = ["name", "name_en", "gender", "category", "email", "phone", "lang"];
const TEMPLATE_EXAMPLES = [
  ["أحمد سفيان بيرم", "Ahmad Sufian Bayram", "male", "catalyst", "ahmad@example.com", "+963 944 123 456", "ar"],
  ["Lina Haddad", "Lina Haddad", "female", "vip", "lina@example.com", "+971 50 123 4567", "en"],
];
const isExampleRow = (r: InvitationRow) => /@example\.com$/i.test((r.email ?? "").trim());

function downloadTemplate() {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = [TEMPLATE_HEADER, ...TEMPLATE_EXAMPLES].map((r) => r.map(esc).join(","));
  // BOM so Excel opens the Arabic correctly.
  const blob = new Blob(["\uFEFF" + lines.join("\r\n") + "\r\n"], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "awards_guests_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function exportCsv(rows: AwardsInvitation[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ["Name", "Name (EN)", "Gender", "Category", "Email", "Phone", "Lang", "Status", "Emailed", "WhatsApp sent", "Opened", "Answer", "Answered at", "Link", "Message"];
  const body = rows.map((r) =>
    [r.fullName, r.fullNameEn, r.gender, r.category ? CATEGORY_LABEL[r.category] : "", r.email, r.phone, r.lang, STATUS_LABEL[r.status], r.emailSentAt, r.whatsappSentAt, r.openedAt, r.rsvp, r.rsvpAt, r.link, inviteMessage(r)].map(esc).join(","),
  );
  const blob = new Blob(["﻿" + [header.join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `awards_invitations_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
